import { useCallback, useEffect, useRef, useState } from 'react';
import { log } from '@/logger/logger';
import { isFigmaEmbedAllowedOrigin } from '@/consts/auth.consts';
import { FIGMA_EMBED_MESSAGE_TYPES } from '@/consts/figma-embed-messages';
import { isCommunityPreviewOptionalAuth } from '@/utils/auth.utils';
import {
  decodeFigmaEmbedToken,
  getFigmaEmbedUserId,
  isAnonymousEmbedViewer,
  normalizeWeavePricingTier,
  type FigmaEmbedTokenPayload,
} from './figmaEmbedToken';
import type { AuthUser } from '@/types/auth.types';
import type { AuthProviderHook, SignInResult } from '../authProvider.types';

export type { FigmaEmbedPermission } from './figmaEmbedToken';

const logger = log.getLogger('useFigmaEmbedAuthProvider');

const TOKEN_REFRESH_LEAD_MS = 3 * 60 * 1000;
const TOKEN_REQUEST_TIMEOUT_MS = 5_000;

function buildAuthUserFromPayload(
  payload: FigmaEmbedTokenPayload,
  token: string,
  getIdToken: (forceRefresh?: boolean) => Promise<string>,
): AuthUser {
  return {
    uid: getFigmaEmbedUserId(payload.sub),
    email: null,
    // Name/email/photo are not carried by embed tokens. The editor UI surfaces that show these
    // (share modal, Intercom, analytics identify) are hidden or N/A in embed mode.
    displayName: null,
    photoURL: null,
    // Logged-out "Anyone with the link" viewer — deduced from the embed token's
    // empty `sub` (the only token the client decodes; see isAnonymousEmbedViewer).
    isAnonymous: isAnonymousEmbedViewer(payload),
    // Normalized here so every consumer sees a concrete tier rather than an
    // optional claim.
    weavePricingTier: normalizeWeavePricingTier(payload.weave_pricing_tier),
    accessToken: token,
    getIdToken,
  };
}

// TODO(figma-embed): consider refactoring the token fetch/refresh plumbing on top of
// TanStack `useQuery` — the query function wraps the postMessage request/response, a
// `refetchInterval` computed from `payload.exp - TOKEN_REFRESH_LEAD_MS` replaces the manual
// `setTimeout`/`scheduleProactiveRefresh`, and `status` plus the query's built-in in-flight
// dedup would remove the `pendingRequestRef` coalescing logic and the ad-hoc loading state.
// Non-blocking cleanup; leaving the explicit ref-based implementation for now since V1 is
// already verified end-to-end.
export const useFigmaEmbedAuthProvider = (): AuthProviderHook => {
  const isOptionalAuth = isCommunityPreviewOptionalAuth();
  const [user, setUser] = useState<AuthUser | null>(null);
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(!isOptionalAuth);

  const refreshTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const requestTokenRef = useRef<() => Promise<string>>(() => Promise.reject(new Error('not ready')));
  const accessTokenRef = useRef<string | null>(null);
  const pendingRequestRef = useRef<{
    resolve: (token: string) => void;
    reject: (reason: Error) => void;
    timer: ReturnType<typeof setTimeout>;
  } | null>(null);

  useEffect(() => {
    accessTokenRef.current = accessToken;
  }, [accessToken]);

  const clearRefreshTimer = useCallback(() => {
    if (refreshTimerRef.current !== null) {
      clearTimeout(refreshTimerRef.current);
      refreshTimerRef.current = null;
    }
  }, []);

  const scheduleProactiveRefresh = useCallback(
    (expSeconds: number) => {
      clearRefreshTimer();
      const expiresAtMs = expSeconds * 1000;
      const refreshAtMs = expiresAtMs - TOKEN_REFRESH_LEAD_MS;
      const delayMs = Math.max(0, refreshAtMs - Date.now());
      refreshTimerRef.current = setTimeout(() => {
        requestTokenRef.current().catch((error) => {
          logger.error('Proactive token refresh failed', error);
        });
      }, delayMs);
    },
    [clearRefreshTimer],
  );

  const requestTokenFromParent = useCallback((): Promise<string> => {
    if (isOptionalAuth) {
      return Promise.reject(new Error('Community preview optional auth does not request a parent token'));
    }

    if (pendingRequestRef.current) {
      return new Promise((resolve, reject) => {
        const prev = pendingRequestRef.current!;
        const prevResolve = prev.resolve;
        const prevReject = prev.reject;
        prev.resolve = (token) => {
          prevResolve(token);
          resolve(token);
        };
        prev.reject = (reason) => {
          prevReject(reason);
          reject(reason);
        };
      });
    }

    return new Promise((resolve, reject) => {
      if (window.parent === window) {
        reject(new Error('Not running inside an iframe — no parent to request token from'));
        return;
      }

      const timer = setTimeout(() => {
        const pending = pendingRequestRef.current;
        pendingRequestRef.current = null;
        pending?.reject(
          new Error(`Timed out waiting for ${FIGMA_EMBED_MESSAGE_TYPES.inbound.TOKEN_REFRESH} from parent`),
        );
      }, TOKEN_REQUEST_TIMEOUT_MS);

      pendingRequestRef.current = { resolve, reject, timer };
      window.parent.postMessage({ type: FIGMA_EMBED_MESSAGE_TYPES.outbound.TOKEN_REQUEST }, '*');
    });
  }, [isOptionalAuth]);

  useEffect(() => {
    requestTokenRef.current = requestTokenFromParent;
  }, [requestTokenFromParent]);

  // Reactive refresh path: on axios 401, `axiosConfig.ts` calls `currentUser.getIdToken(true)`
  // before retrying. Honoring `forceRefresh` ensures the retry round-trips the parent for a
  // freshly-minted JWT instead of replaying the rejected token. Complements the proactive
  // visibility-change refresh path.
  //
  // `accessTokenRef.current` (not `accessToken` state) keeps this callback stable across
  // renders — the AuthUser stores it once at build time, so a state-in-deps version would
  // freeze a stale closure into that slot.
  const getIdToken = useCallback(
    async (forceRefresh = false): Promise<string> => {
      if (!forceRefresh) {
        const cached = accessTokenRef.current;
        if (cached) {
          const payload = decodeFigmaEmbedToken(cached);
          const notExpired = payload !== null && payload.exp * 1000 > Date.now() + 10_000;
          if (notExpired) return cached;
        }
      }
      return requestTokenFromParent();
    },
    [requestTokenFromParent],
  );

  useEffect(() => {
    if (isOptionalAuth) {
      setUser(null);
      setAccessToken(null);
      setIsLoading(false);
      return undefined;
    }

    const handleMessage = (event: MessageEvent) => {
      if (!isFigmaEmbedAllowedOrigin(event.origin)) {
        return;
      }
      const data = event.data as { type?: string; token?: string } | null;
      if (!data || data.type !== FIGMA_EMBED_MESSAGE_TYPES.inbound.TOKEN_REFRESH || typeof data.token !== 'string') {
        return;
      }

      const token = data.token;
      const payload = decodeFigmaEmbedToken(token);
      const pending = pendingRequestRef.current;

      // Malformed / undecodable token: fail loudly instead of stalling.
      //
      // Resolving the pending promise here would satisfy the initial `requestTokenFromParent()
      // .catch(setIsLoading(false))` without ever flipping `isLoading`, and since the timeout is
      // also cleared on arrival, the app would spin forever with no recovery path. Reject the
      // pending promise and flip `isLoading` so the upstream AuthProviderInner stops rendering
      // the spinner and drops into its unauthenticated branch.
      //
      // TODO(figma-embed): surface a dedicated error state so the UI can show an inline
      // "unable to load Weave — please reload" message instead of the default unauthenticated
      // shape, which in embed mode has no sensible sign-in path.
      if (!payload) {
        logger.error('Received token with invalid payload');
        setIsLoading(false);
        if (pending) {
          clearTimeout(pending.timer);
          pendingRequestRef.current = null;
          pending.reject(new Error('Received malformed token from parent'));
        }
        return;
      }

      setAccessToken(token);
      setUser((currentUser) => {
        const nextUid = getFigmaEmbedUserId(payload.sub);
        if (currentUser?.uid === nextUid) {
          return currentUser;
        }
        return buildAuthUserFromPayload(payload, token, getIdToken);
      });
      setIsLoading(false);
      scheduleProactiveRefresh(payload.exp);

      if (pending) {
        clearTimeout(pending.timer);
        pendingRequestRef.current = null;
        pending.resolve(token);
      }
    };

    window.addEventListener('message', handleMessage);
    requestTokenFromParent().catch((error) => {
      logger.error('Initial token request failed', error);
      setIsLoading(false);
    });

    return () => {
      window.removeEventListener('message', handleMessage);
      clearRefreshTimer();
      const pending = pendingRequestRef.current;
      if (pending) {
        clearTimeout(pending.timer);
        pendingRequestRef.current = null;
        pending.reject(new Error('FigmaEmbedAuthProvider unmounted before token was delivered'));
      }
    };
  }, [clearRefreshTimer, getIdToken, isOptionalAuth, requestTokenFromParent, scheduleProactiveRefresh]);

  // Wake-up refresh: background tabs throttle/freeze `setTimeout`, so the proactive
  // timer may not fire after long idle. Refresh eagerly on visibility return.
  useEffect(() => {
    if (isOptionalAuth) return undefined;

    const handleVisibilityChange = () => {
      if (document.visibilityState !== 'visible') return;

      const token = accessTokenRef.current;
      const payload = token ? decodeFigmaEmbedToken(token) : null;
      const notExpiring = payload !== null && payload.exp * 1000 > Date.now() + TOKEN_REFRESH_LEAD_MS;
      if (notExpiring) return;

      requestTokenRef.current().catch((error) => {
        logger.error('Wake-up token refresh failed', error);
      });
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [isOptionalAuth]);

  const signIn = useCallback((): Promise<SignInResult> => {
    return Promise.reject(new Error('signIn is not supported in Figma embed mode'));
  }, []);

  const signOut = useCallback(() => {
    clearRefreshTimer();
    setUser(null);
    setAccessToken(null);
    return Promise.resolve();
  }, [clearRefreshTimer]);

  const getCurrentUser = useCallback(() => user, [user]);

  const getAdditionalUserInfo = useCallback(() => null, []);

  const getStoredPromotion = useCallback(() => null, []);

  const initiateRedirectSignIn = useCallback(() => {
    return Promise.reject(new Error('initiateRedirectSignIn is not supported in Figma embed mode'));
  }, []);

  return {
    signIn,
    signOut,
    initiateRedirectSignIn,
    getIdToken,
    getCurrentUser,
    getAdditionalUserInfo,
    getStoredPromotion,
    user,
    isLoading,
    accessToken,
  };
};
